'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { CreditCard, LogOut, Crown, Loader2 } from 'lucide-react'
import { useSubscription } from '@/hooks/use-subscription'
import { useUser } from '@/hooks/use-user'
import { UpgradeModal } from '@/components/subscription/upgrade-modal'
import { toast } from '@/components/ui'
import { trackAction } from '@/lib/tracking'

interface UserDropdownProps {
  className?: string
}

export function UserDropdown({ className = '' }: UserDropdownProps) {
  const [isLoadingBilling, setIsLoadingBilling] = useState(false)
  const [upgradeModal, setUpgradeModal] = useState<{
    openTrigger: 'upgrade_button'
  } | null>(null)
  const router = useRouter()
  const { user } = useUser()
  const { hasSubscription, subscriptionDetails, redirectToBilling } = useSubscription()

  if (!user) return null

  const handleSignOut = async () => {
    const { createClient } = await import('@/lib/supabase/client')
    const supabase = createClient()
    await supabase.auth.signOut()
    router.refresh()
  }

  const handleBillingPortal = async () => {
    setIsLoadingBilling(true)
    try {
      await redirectToBilling()
    } catch (error) {
      console.error('Error accessing billing portal:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to access billing portal')
    } finally {
      setIsLoadingBilling(false)
    }
  }

  const handleUpgradeClick = () => {
    setUpgradeModal({ openTrigger: 'upgrade_button' })
    trackAction('upgrade_modal_triggered', {
      source: 'user_dropdown',
      trigger: 'upgrade_button'
    })
  }

  const getSubscriptionStatus = () => {
    if (!hasSubscription) return 'Free Plan'
    
    // Check if we have subscription details
    if (!subscriptionDetails?.plan) return 'Pro Plan'
    
    // Map common Stripe price patterns to user-friendly names
    const planId = subscriptionDetails.plan.toLowerCase()
    
    // Check for monthly plans
    if (planId.includes('month') || planId.includes('mo') || planId.includes('19')) {
      return 'Pro Monthly'
    }
    
    // Check for annual plans
    if (planId.includes('year') || planId.includes('annual') || planId.includes('yr') || planId.includes('99')) {
      return 'Pro Annual'
    }
    
    // Fallback to generic Pro Plan
    return 'Pro Plan'
  }

  // Get display name from user metadata or email
  const getDisplayName = () => {
    if (user.user_metadata?.name) {
      return user.user_metadata.name
    }
    if (user.user_metadata?.full_name) {
      return user.user_metadata.full_name
    }
    // Fallback to email username
    return user.email?.split('@')[0] || 'User'
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={cn("flex items-center justify-center", className)}>
            <Avatar>
              <AvatarImage src={user.user_metadata?.avatar_url} />
              <AvatarFallback>{user.email?.[0].toUpperCase()}</AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {getDisplayName()}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <div className="flex items-center justify-between px-2 py-1.5">
            <div className="flex items-center">
              <Crown className="mr-2 h-4 w-4" />
              <span className="text-sm">Plan</span>
            </div>
            <Badge variant={hasSubscription ? "default" : "secondary"}>
              {getSubscriptionStatus()}
            </Badge>
          </div>
          
          {hasSubscription && subscriptionDetails?.currentPeriodEnd && (
            <div className="px-2 pb-1">
              <p className="text-xs text-muted-foreground">
                Renews {new Date(subscriptionDetails.currentPeriodEnd).toLocaleDateString()}
              </p>
            </div>
          )}

          <DropdownMenuItem 
            onClick={handleBillingPortal}
            disabled={isLoadingBilling}
            className="flex items-center cursor-pointer"
          >
            {isLoadingBilling ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CreditCard className="mr-2 h-4 w-4" />
            )}
            <span>{hasSubscription ? 'Billing & Subscription' : 'Billing'}</span>
          </DropdownMenuItem>

          {!hasSubscription && (
            <DropdownMenuItem 
              onClick={handleUpgradeClick}
              className="flex items-center cursor-pointer"
            >
              <Crown className="mr-2 h-4 w-4" />
              <span>Upgrade to Pro</span>
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            onClick={handleSignOut}
            className="flex items-center cursor-pointer text-red-600 focus:text-red-600"
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <UpgradeModal
        open={upgradeModal !== null}
        onClose={() => setUpgradeModal(null)}
        openTrigger={upgradeModal?.openTrigger}
      />
    </>
  )
} 